import React from 'react';
import * as Yup from 'yup';
import ModuleContent from './ModuleContent';
import { Application, JsonHelper } from '../../helpers';
import ModuleTypeEnum from '../../enums/ModuleTypeEnum';
import ProductFormError from '../../components/product/errors/ProductFormError';
import StandardTextFormEditorComponent from '../../components/basics/form/StandardTextFormEditorComponent';
import ModuleHelper from '../../helpers/ModuleHelper';

export default class Module {
    id;
    type;
    settings = {};
    additionalFields = [];
    website;

    static ADDITIONAL_FIELD_PREFIX = 'additionalfield_value__';

    // An inactive additional field is hidden, its saved values are kept by the API
    static activeAdditionalFields = (fields) => (fields || []).filter(field => field.active === undefined || !!field.active);

    static schema_add = (model) => {
        let settings = {
            module: Yup.string().when("use_categories", (show, schema) => {
                return show ? schema.required('module') : schema;
            }),

            reference_module: Yup.string().when("use_reference", (show, schema) => {
                return show ? schema.required('reference_module') : schema;
            }),

            reference_modules: Yup.string().when("use_references", (show, schema) => {
                return show ? schema.required('modules') : schema;
            }),

            themes: Yup.string().when('use_themes', (show, schema) => {
                return show ? schema.transform((value) => value ?? '').required('themes') : schema;
            }),

            image_width: Yup.string().when("show_image", (show, schema) => {
                return show ? schema.required('image_width').matches(/^[0-9]+$/, "image_width.number") : schema;
            }),
            image_height: Yup.string().when("show_image", (show, schema) => {
                return show ? schema.required('image_height').matches(/^[0-9]+$/, "image_height.number") : schema;
            }),

            content_with_blocks_image_width: Yup.string().when("content_with_blocks", (show, schema) => {
                return show ? schema.required('content_with_blocks_image_width').matches(/^[0-9]+$/, "content_with_blocks_image_width.number") : schema;
            }),
            content_with_blocks_image_height: Yup.string().when("content_with_blocks", (show, schema) => {
                return show ? schema.required('content_with_blocks_image_height').matches(/^[0-9]+$/, "content_with_blocks_image_height.number") : schema;
            }),
            cover_image_width: Yup.string().when("show_cover_image", (show, schema) => {
                return show ? schema.required('cover_image_width').matches(/^[0-9]+$/, "cover_image_width.number") : schema;
            }),
            cover_image_height: Yup.string().when("show_cover_image", (show, schema) => {
                return show ? schema.required('cover_image_height').matches(/^[0-9]+$/, "cover_image_height.number") : schema;
            }),
            header_img_width: Yup.string().when("need_header_img", (show, schema) => {
                return show ? schema.required('header_img_width').matches(/^[0-9]+$/, "header_img_width.number") : schema;
            }),
            header_img_height: Yup.string().when("need_header_img", (show, schema) => {
                return show ? schema.required('header_img_height').matches(/^[0-9]+$/, "header_img_height.number") : schema;
            }),

            subcategories_themes: Yup.string().when('subcategories_use_themes', (show, schema) => {
                return show ? schema.transform((value) => value ?? '').required('subcategories_themes') : schema;
            }),
            subcategories_image_width: Yup.string().when("subcategories_content_with_blocks", (show, schema) => {
                return show ? schema.required('subcategories_image_width').matches(/^[0-9]+$/, "subcategories_image_width.number") : schema;
            }),
            subcategories_image_height: Yup.string().when("subcategories_content_with_blocks", (show, schema) => {
                return show ? schema.required('subcategories_image_height').matches(/^[0-9]+$/, "subcategories_image_height.number") : schema;
            }),

            subcategories_cover_image_width: Yup.string().when("subcategories_show_cover_image", (show, schema) => {
                return show ? schema.required('subcategories_cover_image_width').matches(/^[0-9]+$/, { message: "subcategories_cover_image_width.number", excludeEmptyString: true }) : schema;
            }),
            subcategories_cover_image_height: Yup.string().when("subcategories_show_cover_image", (show, schema) => {
                return show ? schema.required('subcategories_cover_image_height').matches(/^[0-9]+$/, { message: "subcategories_cover_image_height.number", excludeEmptyString: true }) : schema;
            }),
            subcategories_header_img_width: Yup.string().when("subcategories_need_header_img", (show, schema) => {
                return show ? schema.required('subcategories_header_img_width').matches(/^[0-9]+$/, { message: "subcategories_header_img_width.number", excludeEmptyString: true }) : schema;
            }),
            subcategories_header_img_height: Yup.string().when("subcategories_need_header_img", (show, schema) => {
                return show ? schema.required('subcategories_header_img_height').matches(/^[0-9]+$/, { message: "subcategories_header_img_height.number", excludeEmptyString: true }) : schema;
            }),

            media_password: Yup.string().when("hide_media", (hide, schema) => {
                return hide ? schema.required('media_password').min(12, 'media_password|12|min') : schema;
            }),

            use_membership_duration: Yup.bool().when(['use_membership', 'use_membership_start_date'], {
                is: (use_membership, use_membership_start_date) => use_membership && !use_membership_start_date,
                then: Yup.bool().oneOf([true], 'use_membership_duration|use_membership_start_date|required.conditional')
            }),
            use_membership_start_date: Yup.bool().when(['use_membership', 'use_membership_duration'], {
                is: (use_membership, use_membership_duration) => use_membership && !use_membership_duration,
                then: Yup.bool().oneOf([true], 'use_membership_duration|use_membership_start_date|required.conditional')
            }),

            membership_duration: Yup.string().when("use_membership_duration", (hide, schema) => {
                return hide ? schema.required('membership_duration') : schema;
            }),
            membership_start_date: Yup.string().nullable().when("use_membership_start_date", (hide, schema) => {
                // Jaarlijks terugkerende dag en maand (MM-DD), oude waarden (YYYY-MM-DD) blijven geldig. Geen 29 februari.
                return hide ? schema.required('membership_start_date')
                    .test('month-day', 'membership_start_date|invalid', value => {
                        const match = /^(?:\d{4}-)?(\d{2})-(\d{2})$/.exec(value || '');
                        if (!match) return false;
                        const month = parseInt(match[1], 10), day = parseInt(match[2], 10);
                        return month >= 1 && month <= 12 && day >= 1 && day <= new Date(2023, month, 0).getDate();
                    }) : schema;
            }),
            membership_price: Yup.string().when("use_membership", (hide, schema) => {
                return hide ? schema.required('membership_price').matches(/^\d*[.,,]{0,1}\d*$/, 'membership_price|number') : schema;
            }),

            additional_gdpr_consent_1_text: Yup.string().when("show_additional_gdpr_consent_1", (show, schema) => {
                return show ? schema.required('additional_gdpr_consent_1_text') : schema;
            }),
            additional_gdpr_consent_2_text: Yup.string().when("show_additional_gdpr_consent_2", (show, schema) => {
                return show ? schema.required('additional_gdpr_consent_2_text') : schema;
            }),

            show_minimum_age: Yup.bool().when(['use_age_requirements', 'show_maximum_age'], {
                is: (use_age_requirements, show_maximum_age) => use_age_requirements && !show_maximum_age,
                then: Yup.bool().oneOf([true], 'show_minimum_age|show_maximum_age|required.conditional')
            }),
            show_maximum_age: Yup.bool().when(['use_age_requirements', 'show_minimum_age'], {
                is: (use_age_requirements, show_minimum_age) => use_age_requirements && !show_minimum_age,
                then: Yup.bool().oneOf([true], 'show_minimum_age|show_maximum_age|required.conditional')
            }),
            switch_date: Yup.string().when("use_age_requirements", (hide, schema) => {
                return hide ? schema.required('switch_date') : schema;
            })

            /*switch_date: Yup.string().test("is-day-month", "switch_date", val => {
                if (!val) return true;

                let schemaUrl = Yup.object().shape({
                    switch_date: Yup.string().matches(/(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[1,2])/, 'switch_date|invalid')
                });

                return schemaUrl.validate({ switch_date: val });
            }).nullable()*/
        };

        if (model.type === ModuleTypeEnum.MAP) {
            settings.pages = Yup.array().required('pages|select');
        }

        return Yup.object({
            name: Yup.string().required('CMS.Modules.Form.name').max(30, 'CMS.Modules.Form.name|30|max'),
            description: Yup.string().max(500, 'CMS.Modules.Form.description|500|max').nullable(),
            type: Yup.mixed().required('CMS.Modules.Form.type|select'),
            settings: Yup.object().shape(settings, [['use_membership_duration', 'use_membership_start_date'], ['use_membership_duration', 'use_membership_start_date'], ['show_minimum_age', 'show_maximum_age'], ['show_minimum_age', 'show_maximum_age']])
        });
    }

    static fromJson = (json) => {
        let module = new Module();
        module.id = json.id;
        module.type = json.type;
        module.website = json.website;
        module.settings = json.settings === "[]" ? "{}" : JsonHelper.tryParse(json.settings);
        module.content = json.contents.map(content => ModuleContent.fromJson(content));
        module.additionalFields = Module.activeAdditionalFields(json.additional_fields);
        return module;
    }

    isOfType = (type) => this.type === type;
    getSetting = (key, def = null) => this.settings[key] || def;

    static getSettings(module = null) {
        if (!module) module = this.get().module;
        if (!module) return [];

        if (module.settings === "[]") return [];
        return JsonHelper.tryParse(module.settings);
    }

    static getSetting(module, setting, def = null) {
        let settings = this.getSettings(module);

        if (!settings[setting]) return def;
        return settings[setting];
    }

    static hasContent(module) {
        let settings = this.getSettings(module);

        if (settings.content_with_blocks || settings.show_content) {
            return true;
        }

        if (!settings.has_subcategories) {
            return false;
        }

        return !!settings.subcategories_show_content;
    }

    static getContent(language, module = null) {
        if (!module) {
            module = this.get();
        }

        if (!module) return {};

        return module.module.contents.filter(content => content.language === language)[0];
    }

    static get = (settings = {}) => {
        let module = localStorage.getItem('module');
        if (!module) return null;

        module = JSON.parse(module);

        if (settings.asModel) {
            module = Module.fromJson(module.module);
        }

        return module;
    }

    static getId = () => {
        let module = this.get();
        if (!module) return null;

        return module.id;
    }

    static set = (module) => localStorage.setItem('module', JSON.stringify(module));

    static remove = () => localStorage.removeItem('module');

    static getIcon = (module) => {
        let settings = JSON.parse(module.settings);
        return settings.icon ? settings.icon : 'cubes';
    }

    static getRoute = (module) => {
        let settings = JSON.parse(module.settings);
        return settings.route ? settings.route : '';
    }

    static change = (newModule) => {
        this.remove();

        Application.resetTablePageNumber();

        if (!newModule) return;

        this.set(newModule);
        ModuleHelper.select(newModule.id);

        let e = null;
        if (typeof window.CustomEvent === 'function') {
            e = new CustomEvent('moduleChanged');
        } else {
            e = document.createEvent('CustomEvent');
            e.initCustomEvent('moduleChanged', true, false);
        }

        window.dispatchEvent(e);
    }

    static getReferencingModules = (categoryModuleId, modules) => modules.filter(module => {
        let settings = JsonHelper.tryParse(module.module.settings);
        return settings.use_categories && parseInt(settings.module) === parseInt(categoryModuleId);
    });

    static filterReferences = (modules) => modules.filter(module => [ModuleTypeEnum.CATEGORY, ModuleTypeEnum.PROJECT, ModuleTypeEnum.WEBLINK, ModuleTypeEnum.TEAM].indexOf(module.module.type) > -1)
        .filter(module => {
            if (module.module.type !== ModuleTypeEnum.CATEGORY) return module;
            let settings = JsonHelper.tryParse(module.module.settings);

            let hasContent = settings.show_content || settings.content_with_blocks;
            if (settings.has_subcategories) {
                hasContent = hasContent || settings.subcategories_show_content || settings.subcategories_content_with_blocks
            }
            return hasContent ? module : null;
        });

    static renderSubMenu = (module) => {
        if ([ModuleTypeEnum.EVENT].indexOf(module.type) < 0) return false;

        let settings = JsonHelper.tryParse(module.settings);

        switch (module.type) {
            case ModuleTypeEnum.EVENT:
                return settings?.use_attendees;
            default:
                return true;
        }
    };

    static fillModel = (module, lng, model) => {
        if (module.module.additional_fields?.length <= 0) return model;

        let values = {};
        if (model.contents) {
            values = model.contents.find(c => c.language === lng)?.additionalFieldValues;
        } else {
            values = model.additionalFieldValues;
        }

        Module.activeAdditionalFields(module.module.additional_fields).forEach(field => {
            switch (field.field_type) {
                case 'editor':
                    model[Module.ADDITIONAL_FIELD_PREFIX + field.field_key] = values[field.field_key] ? JsonHelper.tryParse(values[field.field_key]) : null;
                    break;
                default:
                    model[Module.ADDITIONAL_FIELD_PREFIX + field.field_key] = values[field.field_key] ? values[field.field_key] : '';

                    if (field.has_prefix_choices) {
                        model[Module.ADDITIONAL_FIELD_PREFIX + field.field_key + '__prepend'] = values[field.field_key + '__prepend'] ? values[field.field_key + '__prepend'] : null;
                    }
                    if (field.has_suffix_choices) {
                        model[Module.ADDITIONAL_FIELD_PREFIX + field.field_key + '__append'] = values[field.field_key + '__append'] ? values[field.field_key + '__append'] : null;
                    }
                    break;
            }
        });

        return model;
    }

    static renderAdditionalFields = ({ module, model, lng }, fields) => {
        if (module.module.additional_fields?.length <= 0) return fields;

        let values = {};
        if (model.contents) {
            values = model.contents?.find(c => c.language === lng)?.additionalFieldValues;
        } else {
            values = model.additionalFieldValues;
        }

        Module.activeAdditionalFields(module.module.additional_fields).forEach(field => {
            let content = field.contents.find(c => c.language === lng);

            switch (field.field_type) {
                case 'editor':
                    fields.push({
                        type: "col",
                        md: 12,
                        fields: [],
                        custom: StandardTextFormEditorComponent,
                        customProps: {
                            valueField: Module.ADDITIONAL_FIELD_PREFIX + field.field_key,
                            label: content.label,
                            optional: !field.is_required,
                            message: content.help_text
                        }
                    });
                    break;
                case 'select':
                    fields.push({
                        type: "col",
                        md: 12,
                        fields: [
                            {
                                title: content.label,
                                type: "select",
                                name: Module.ADDITIONAL_FIELD_PREFIX + field.field_key,
                                optional: !field.is_required,
                                placeholder: "CMS.Modules.AdditionalField.field.select.placeholder",
                                placeholderValues: { field: content.label },
                                message: content.help_text,
                                options: field.options.map(option => {
                                    let content = option.contents.find(oc => oc.language === lng);
                                    return { value: option.id, label: content.label };
                                }).filter(x => x !== false),
                                customFeedback: ProductFormError,
                                formErrorName: Module.ADDITIONAL_FIELD_PREFIX + field.field_key
                            }
                        ]
                    });
                    break;
                case 'textarea':
                default:
                    let additionalField = {
                        title: content.label,
                        type: field.field_type,
                        name: Module.ADDITIONAL_FIELD_PREFIX + field.field_key,
                        placeholder: "CMS.Modules.AdditionalField.field.text.placeholder",
                        placeholderValues: { field: content.label },
                        optional: !field.is_required,
                        message: content.help_text,
                        customFeedback: ProductFormError,
                        customProps: {},
                        formErrorName: Module.ADDITIONAL_FIELD_PREFIX + field.field_key
                    };

                    if (field.has_prefix_choices && content.prefix_choices) {
                        let prefixChoices = content.prefix_choices.split(';').filter(c => c);

                        let prefixOptions = [
                            <option value="">-</option>
                        ];

                        prefixChoices.forEach(choice => prefixOptions.push(<option value={choice} key={choice}>{choice ? choice : '-'}</option>));

                        additionalField.customProps.prepend = {
                            name: Module.ADDITIONAL_FIELD_PREFIX + field.field_key + '__prepend',
                            disabled: false,
                            selected: values[field.field_key + '__prepend'] ?? prefixChoices[0],
                            options: prefixOptions
                        }
                    }

                    if (field.has_suffix_choices && content.suffix_choices) {
                        let suffixChoices = content.suffix_choices.split(';').filter(c => c);

                        let suffixOptions = [
                            <option value="">-</option>
                        ];

                        suffixChoices.forEach(choice => suffixOptions.push(<option value={choice} key={choice}>{choice ? choice : '-'}</option>))

                        additionalField.customProps.append = {
                            name: Module.ADDITIONAL_FIELD_PREFIX + field.field_key + '__append',
                            disabled: false,
                            selected: values[field.field_key + '__append'] ?? suffixChoices[0],
                            options: suffixOptions
                        }
                    }

                    fields.push({
                        type: "col",
                        md: 12,
                        fields: [additionalField]
                    });
                    break;
            }
        });

        return fields;
    }

    static validateAdditionalField = (module, lng, fields) => {
        if (module.module.additional_fields?.length <= 0) return fields;

        Module.activeAdditionalFields(module.module.additional_fields).forEach(field => {
            let content = field.contents.find(c => c.language === lng);

            let validationObject = null;

            switch (field.field_type) {
                case 'editor':
                    validationObject = Yup.object({ text: Yup.string() });
                    break;
                case 'numeric':
                    validationObject = Yup.number().transform((value, originalValue) => {
                        if (typeof originalValue !== 'string') {
                            return value;
                        }

                        const cleaned = originalValue.replace(/,/g, '').trim();

                        // volledige string moet een geldig getal zijn
                        if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
                            return NaN;
                        }

                        return Number(cleaned);
                    }).typeError(content.label + '|number');
                    break;
                case 'url':
                    validationObject = Yup.string().test("is-url-without-https-http", content.label + '|invalid', value => {
                        if (!value || value.indexOf('/permalink/') > -1) return true;

                        return Yup.object().shape({
                            [Module.ADDITIONAL_FIELD_PREFIX + field.field_key]: Yup.string().url(content.label + '|invalid')
                        }).validate({ [Module.ADDITIONAL_FIELD_PREFIX + field.field_key]: 'https://' + value.replace('http://', '').replace('https://', '').replace('www.', '') })
                    });
                    break;
                default:
                    validationObject = Yup.string().max(255, content.label + '|255|max');
                    break;
            }

            if (field.is_required) {
                validationObject = validationObject.required(content.label);
            } else {
                validationObject = validationObject.nullable();
            }

            fields[Module.ADDITIONAL_FIELD_PREFIX + field.field_key] = validationObject;
        });
        return fields;
    }
}
